/**
 * Cart UI Sync (Vividoshop theme)
 * -----------------------------------------------------------------------------
 * Purpose: Ensure header cart count and cart drawer update after any cart mutation,
 * regardless of which JavaScript (theme, Teeinblue, etc.) makes the request.
 *
 * This script listens (non-blocking) for cart mutations and triggers UI updates.
 * 
 * FEATURE: Redirects to /cart after successful add-to-cart (Vividostore preference)
 * This avoids AJAX cart drawer issues and gives customers a clear next step.
 */
(function() {
  'use strict';

  // Save original references for our own use
  var OriginalXHR = window.XMLHttpRequest;
  var originalFetch = window.fetch;

  // Track last known cart count to avoid unnecessary DOM updates
  var lastKnownCartCount = null;
  
  // CONFIGURATION: Redirect to cart page after successful add-to-cart
  // This ensures the customer sees their cart immediately without drawer issues
  var REDIRECT_TO_CART_AFTER_ADD = true;

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------

  function isCartMutationUrl(url) {
    if (!url || typeof url !== 'string') return false;
    return (
      url.indexOf('/cart/add') !== -1 ||
      url.indexOf('/cart/change') !== -1 ||
      url.indexOf('/cart/update') !== -1 ||
      url.indexOf('/cart/clear') !== -1
    );
  }

  /**
   * Check if the cart add request is for a Teeinblue customized product.
   * We only inject the "✓ Design" property for these products to avoid
   * polluting regular product line items.
   */
  function isTeeinblueProduct(data) {
    if (!data) return false;
    
    // Check for _customization_id in properties (indicates Teeinblue product)
    if (typeof data === 'object' && data.properties) {
      return '_customization_id' in data.properties || 
             '_customization_image' in data.properties;
    }
    
    // Check JSON string
    if (typeof data === 'string') {
      return data.indexOf('_customization_id') !== -1 ||
             data.indexOf('_customization_image') !== -1;
    }
    
    // Check FormData
    if (data instanceof FormData) {
      var hasCustomization = false;
      // FormData.entries() may not be available in old browsers, use get() as fallback
      if (typeof data.get === 'function') {
        hasCustomization = data.get('properties[_customization_id]') !== null ||
                          data.get('properties[_customization_image]') !== null;
      }
      return hasCustomization;
    }
    
    return false;
  }

  /**
   * Updates all cart count UI elements.
   */
  function applyCartCount(itemCount) {
    if (itemCount === lastKnownCartCount) return;
    lastKnownCartCount = itemCount;

    // Update ALL elements with id="CartCount" (theme may have duplicates)
    var cartCountElements = document.querySelectorAll('[id="CartCount"]');
    for (var i = 0; i < cartCountElements.length; i++) {
      cartCountElements[i].textContent = itemCount;
    }

    // Cart drawer counter
    var canvasCount = document.getElementById('CartCountCavas');
    if (canvasCount) {
      canvasCount.textContent = itemCount;
    }
  }

  /**
   * Fetch /cart.js and update cart counters only.
   * 
   * NOTE: We intentionally do NOT refresh the mini-cart drawer HTML here.
   * The theme's nuranium.js already handles drawer updates via its own
   * /cart?view=json fetch. Having both scripts update the drawer causes:
   * - Double network requests
   * - Race conditions / flickering
   * - Unnecessary DOM thrashing
   * 
   * This script focuses solely on keeping header cart counts in sync.
   */
  function syncFromCartJson() {
    var countRequest = new OriginalXHR();
    countRequest.open('GET', '/cart.js?t=' + Date.now(), true);
    countRequest.setRequestHeader('Accept', 'application/json');

    countRequest.onload = function() {
      if (countRequest.status >= 200 && countRequest.status < 400) {
        try {
          var cart = JSON.parse(countRequest.responseText);
          if (cart && typeof cart.item_count === 'number') {
            applyCartCount(cart.item_count);
          }
        } catch (e) {
          console.error('[Cart Sync] Error parsing /cart.js:', e);
        }
      }
    };
    countRequest.send();
  }

  // ---------------------------------------------------------------------------
  // Throttled sync scheduler
  // ---------------------------------------------------------------------------
  var syncScheduled = false;

  function scheduleSync() {
    if (syncScheduled) return;
    syncScheduled = true;

    setTimeout(function() {
      syncScheduled = false;
      syncFromCartJson();
    }, 500);
  }

  // ---------------------------------------------------------------------------
  // Hook 1: Native fetch() API
  // - For /cart/add: inject checkout property, then sync UI
  // - For other cart mutations: sync UI only
  // ---------------------------------------------------------------------------
  if (typeof originalFetch === 'function') {
    window.fetch = function(input, init) {
      var url = '';
      if (typeof input === 'string') {
        url = input;
      } else if (input instanceof URL) {
        url = input.href;
      } else if (input && typeof input.url === 'string') {
        url = input.url;
      }

      // For /cart/add with Teeinblue products, inject a checkout-visible property
      // Only for personalized products (those with _customization_id)
      var modifiedInit = init;
      if (url.indexOf('/cart/add') !== -1 && init && init.body) {
        try {
          // Handle JSON body
          if (typeof init.body === 'string') {
            try {
              var bodyJson = JSON.parse(init.body);
              // Only inject for Teeinblue customized products
              if (isTeeinblueProduct(bodyJson)) {
                if (!bodyJson.properties) bodyJson.properties = {};
                bodyJson.properties['✓ Design'] = 'Personalized & saved';
                modifiedInit = Object.assign({}, init, { body: JSON.stringify(bodyJson) });
              }
            } catch (e) {
              // Not valid JSON, skip modification
            }
          }
          // Handle FormData body
          else if (init.body instanceof FormData && isTeeinblueProduct(init.body)) {
            var newFormData = new FormData();
            for (var pair of init.body.entries()) {
              newFormData.append(pair[0], pair[1]);
            }
            newFormData.append('properties[✓ Design]', 'Personalized & saved');
            modifiedInit = Object.assign({}, init, { body: newFormData });
          }
        } catch (e) {
          console.warn('[Cart Sync] Could not inject checkout property:', e);
        }
      }

      var fetchPromise = originalFetch.call(this, input, modifiedInit);

      // After cart mutations, sync UI (and optionally redirect to cart)
      if (isCartMutationUrl(url)) {
        fetchPromise.then(function(response) {
          if (response.ok) {
            scheduleSync();
            
            // Redirect to cart page after successful add-to-cart
            // Skip redirect on MGA product pages — MGA handles its own post-add UX
            if (REDIRECT_TO_CART_AFTER_ADD && url.indexOf('/cart/add') !== -1 && !window.mgaConfig) {
              // Short delay to let Teeinblue complete any post-add processing
              setTimeout(function() {
                window.location.href = '/cart';
              }, 300);
            }
          }
          return response;
        }).catch(function() {});
      }

      return fetchPromise;
    };
  }

  // ---------------------------------------------------------------------------
  // Hook 2: jQuery AJAX
  // - For /cart/add: inject checkout property, then sync UI
  // - For other cart mutations: sync UI only
  // ---------------------------------------------------------------------------
  function setupJQueryHook() {
    if (typeof window.jQuery === 'undefined') return;

    var $ = window.jQuery;
    var originalAjax = $.ajax;

    // Prevent double-hooking
    if ($.ajax._cartSyncHooked) return;

    $.ajax = function(urlOrSettings, settings) {
      var opts = typeof urlOrSettings === 'string'
        ? $.extend({}, settings, { url: urlOrSettings })
        : $.extend({}, urlOrSettings);

      var url = opts.url || '';

      // For /cart/add with Teeinblue products, inject checkout property
      if (url.indexOf('/cart/add') !== -1 && opts.data && isTeeinblueProduct(opts.data)) {
        try {
          // Handle object data
          if (typeof opts.data === 'object' && !(opts.data instanceof FormData)) {
            if (!opts.data.properties) opts.data.properties = {};
            opts.data.properties['✓ Design'] = 'Personalized & saved';
          }
          // Handle JSON string data
          else if (typeof opts.data === 'string') {
            try {
              var bodyJson = JSON.parse(opts.data);
              if (!bodyJson.properties) bodyJson.properties = {};
              bodyJson.properties['✓ Design'] = 'Personalized & saved';
              opts.data = JSON.stringify(bodyJson);
            } catch (e) {
              // Not JSON, might be form-encoded - try to append
              opts.data += '&properties[✓ Design]=Personalized & saved';
            }
          }
        } catch (e) {
          console.warn('[Cart Sync] Could not inject checkout property (jQuery):', e);
        }
      }

      var jqXHR = originalAjax.call(this, opts);

      // After cart mutations, sync UI (and optionally redirect to cart)
      if (isCartMutationUrl(url)) {
        jqXHR.done(function() {
          scheduleSync();
          
          // Redirect to cart page after successful add-to-cart
          // Skip redirect on MGA product pages — MGA handles its own post-add UX
          if (REDIRECT_TO_CART_AFTER_ADD && url.indexOf('/cart/add') !== -1 && !window.mgaConfig) {
            setTimeout(function() {
              window.location.href = '/cart';
            }, 300);
          }
        });
      }

      return jqXHR;
    };

    $.ajax._cartSyncHooked = true;
  }

  // Setup jQuery hook immediately and after DOM ready
  setupJQueryHook();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupJQueryHook);
  }
  setTimeout(setupJQueryHook, 100);

  // ---------------------------------------------------------------------------
  // Hook 3: XMLHttpRequest
  // - For /cart/add: inject checkout property, then sync UI
  // - For other cart mutations: sync UI only
  // ---------------------------------------------------------------------------
  var XHRSend = XMLHttpRequest.prototype.send;
  var XHROpen = XMLHttpRequest.prototype.open;

  XMLHttpRequest.prototype.open = function(method, url) {
    this._cartSyncUrl = url;
    this._cartSyncMethod = method;
    XHROpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function(data) {
    var xhr = this;
    var url = xhr._cartSyncUrl || '';
    var modifiedData = data;

    // For /cart/add with Teeinblue products, inject checkout property
    if (url.indexOf('/cart/add') !== -1 && data && isTeeinblueProduct(data)) {
      try {
        // Handle JSON string
        if (typeof data === 'string') {
          try {
            var bodyJson = JSON.parse(data);
            if (!bodyJson.properties) bodyJson.properties = {};
            bodyJson.properties['✓ Design'] = 'Personalized & saved';
            modifiedData = JSON.stringify(bodyJson);
          } catch (e) {
            // Not JSON, might be form-encoded
            modifiedData = data + '&properties[✓ Design]=Personalized & saved';
          }
        }
        // Handle FormData
        else if (data instanceof FormData) {
          var newFormData = new FormData();
          for (var pair of data.entries()) {
            newFormData.append(pair[0], pair[1]);
          }
          newFormData.append('properties[✓ Design]', 'Personalized & saved');
          modifiedData = newFormData;
        }
      } catch (e) {
        console.warn('[Cart Sync] Could not inject checkout property (XHR):', e);
      }
    }

    XHRSend.call(this, modifiedData);

    // After cart mutations, sync UI (and optionally redirect to cart)
    if (isCartMutationUrl(url)) {
      xhr.addEventListener('load', function() {
        if (xhr.status >= 200 && xhr.status < 400) {
          scheduleSync();
          
          // Redirect to cart page after successful add-to-cart
          // Skip redirect on MGA product pages — MGA handles its own post-add UX
          if (REDIRECT_TO_CART_AFTER_ADD && url.indexOf('/cart/add') !== -1 && !window.mgaConfig) {
            setTimeout(function() {
              window.location.href = '/cart';
            }, 300);
          }
        }
      });
    }
  };

  // ---------------------------------------------------------------------------
  // Hook 4: Custom events
  // ---------------------------------------------------------------------------
  var cartEvents = ['cart:updated', 'cart:refresh', 'ajaxCart.afterCartLoad'];
  cartEvents.forEach(function(eventName) {
    document.addEventListener(eventName, function() {
      scheduleSync();
    });
  });

  // ---------------------------------------------------------------------------
  // Mini-cart drawer refresh (for MGA direct AJAX adds)
  // ---------------------------------------------------------------------------
  // nuranium.js's own AJAX handler only refreshes the drawer when IT initiates
  // the /cart/add call. MGA products use a direct fetch() to /cart/add.js,
  // bypassing nuranium.js entirely. This function fills that gap by fetching
  // the drawer template and injecting it into #cart-info.

  function refreshMiniCartDrawer() {
    var cartInfo = document.getElementById('cart-info');
    if (!cartInfo) return;

    var drawerRequest = new OriginalXHR();
    drawerRequest.open('GET', '/cart?view=json&t=' + Date.now(), true);
    drawerRequest.setRequestHeader('Accept', 'text/html');
    drawerRequest.onload = function() {
      if (drawerRequest.status >= 200 && drawerRequest.status < 400) {
        cartInfo.innerHTML = drawerRequest.responseText;
        // Re-bind theme's quantity +/- handlers if nuranium exposes them
        if (window.nuranium && typeof window.nuranium.PopupAddToCart === 'function') {
          try { window.nuranium.PopupAddToCart(); } catch (e) {}
        }
        // Re-bind currency conversion if active
        if (window.Currency && typeof window.Currency.convertAll === 'function') {
          try { window.Currency.convertAll(window.shopCurrency, jQuery('[name=currencies]').val()); } catch (e) {}
        }
      }
    };
    drawerRequest.send();
  }

  // ---------------------------------------------------------------------------
  // Global API for manual triggers
  // ---------------------------------------------------------------------------
  window.VividoCartSync = window.VividoCartSync || {};
  window.VividoCartSync.sync = syncFromCartJson;
  window.VividoCartSync.scheduleSync = scheduleSync;
  window.VividoCartSync.refreshDrawer = refreshMiniCartDrawer;

})();
